import type { FC, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useMemo } from "react";
import { usePermissions } from "@hooks/usePermissions";
import { useProfile } from "@features/Profile/services/GetProfile";
import {
	getRequiredPermissionForPath,
	getAllPermissionsForPath,
	getFirstAllowedRoute,
	userHasHomeDashboardAccess,
	dashboardAccessPendingProfile,
	shouldShowDashboardNoAccessShell,
	pathRequiresExplicitPermission,
} from "@utils/navigation";
import { permissionListIncludes } from "@utils/permissionIdentifiers";
import { PERMISSIONS } from "@constants/user.roles";
import { BROWSER_ROUTES } from "@constants/browser.routes";
import { EVENT_MANAGEMENT_BROWSER_ROUTES } from "@features/EventManagement/integration/browser.routes";
import {
	canAccessEventsCloneModule,
	canAccessEventsRequestModule,
} from "@features/EventManagement/integration/permissions";
import { isCmAdminUser } from "@features/CmDashboard/cmAdmin";

interface RoutePermissionGuardProps {
	children: ReactNode;
}

/**
 * Guards authenticated routes by permission. If the user navigates to a path they don't have permission for
 * (e.g. by typing the URL), they are redirected so the URL does not stay on the disallowed path.
 */
const RoutePermissionGuard: FC<RoutePermissionGuardProps> = ({ children }) => {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const { userPermissions, hasPermission, userRoles, user } = usePermissions();
	const { data: profile, isLoading: isProfileLoading } = useProfile();

	const canAccessEventsRequestScreens = useMemo(
		() =>
			canAccessEventsRequestModule({
				roles: userRoles,
				organizationTypeId: profile?.organizationTypeId,
				userTypeName: user?.userType?.name,
				userPermissions: userPermissions ?? [],
			}),
		[userRoles, profile?.organizationTypeId, user?.userType?.name, userPermissions],
	);

	const psdaDashboardContext = useMemo(
		() => ({
			organizationTypeId: profile?.organizationTypeId,
			roles: userRoles,
			userTypeName: user?.userType?.name,
		}),
		[profile?.organizationTypeId, userRoles, user?.userType?.name],
	);

	const requiredPermission = getRequiredPermissionForPath(pathname);
	const pathKnown = requiredPermission != null;
	const allPermsForPath = getAllPermissionsForPath(pathname);

	const isEventsRequestPath = pathname === EVENT_MANAGEMENT_BROWSER_ROUTES.EVENTS_REQUEST;
	const isEventsClonePath = pathname === EVENT_MANAGEMENT_BROWSER_ROUTES.EVENTS_CLONE;
	const isRestrictedEventManagementPath = isEventsRequestPath || isEventsClonePath;
	const psdaEventGatePending = isRestrictedEventManagementPath && isProfileLoading;
	const psdaEventAllowed =
		!isRestrictedEventManagementPath ||
		(isEventsRequestPath && canAccessEventsRequestScreens) ||
		(isEventsClonePath && canAccessEventsCloneModule(userPermissions ?? []));

	const isCmAdmin = isCmAdminUser(user);
	const isDashboardPath = pathname === BROWSER_ROUTES.DASHBOARD || pathname === "/";
	const dashboardGatePending =
		isDashboardPath &&
		isProfileLoading &&
		dashboardAccessPendingProfile(userPermissions ?? [], psdaDashboardContext);

	const showNoAccessShell = shouldShowDashboardNoAccessShell(
		userPermissions ?? [],
		psdaDashboardContext,
	);

	const requiresExplicitPermission = pathRequiresExplicitPermission(pathname);
	const hasAllReadBypass =
		!requiresExplicitPermission && permissionListIncludes(userPermissions ?? [], PERMISSIONS.ALL_READ);

	const hasRequired =
		pathKnown &&
		psdaEventAllowed &&
		(hasAllReadBypass ||
			(isDashboardPath
				? userHasHomeDashboardAccess(userPermissions ?? [], psdaDashboardContext) || showNoAccessShell
				: allPermsForPath.some(p =>
						requiresExplicitPermission
							? permissionListIncludes(userPermissions ?? [], p)
							: hasPermission(p as (typeof PERMISSIONS)[keyof typeof PERMISSIONS]),
					)));
	const allowed = pathKnown && hasRequired;

	useEffect(() => {
		if (isCmAdmin) {
			if (!isDashboardPath) navigate(BROWSER_ROUTES.DASHBOARD, { replace: true });
			return;
		}
		if (psdaEventGatePending || dashboardGatePending) return;
		if (allowed) return;

		const firstAllowed = getFirstAllowedRoute(userPermissions || [], psdaDashboardContext);
		if (firstAllowed && firstAllowed !== pathname) {
			navigate(firstAllowed, { replace: true });
			return;
		}
		if (!isDashboardPath) {
			navigate(BROWSER_ROUTES.DASHBOARD, { replace: true });
		}
	}, [
		allowed,
		pathname,
		userPermissions,
		navigate,
		psdaEventGatePending,
		dashboardGatePending,
		psdaDashboardContext,
		isDashboardPath,
		isCmAdmin,
	]);

	if (isCmAdmin) {
		return isDashboardPath ? <>{children}</> : null;
	}

	if (psdaEventGatePending || dashboardGatePending) return null;
	if (!allowed) return null;

	return <>{children}</>;
};

export default RoutePermissionGuard;
